import { ChevronDown, UserPlus } from "lucide-react";
import { useEffect, useMemo, type FC } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { LawyerDTO } from "../../../api/types";
import { usePracticeTeamMembers } from "../../../hooks/usePracticeTeamMembers";
import { useUserRoleAndSubscription } from "../../../hooks/useUserRoleAndSubscription";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";
import { AssignedUsersAvatarStack } from "../../foreignNationals/components/AssignedUsersAvatarStack";
import { displayName, initialsOf } from "../header/clientHeaderModel";

const lawyerLabel = (m: LawyerDTO) =>
  displayName([m.firstName, m.middleName, m.lastName]) ||
  (m.email ?? "").trim();

type Props = {
  id?: string;
  value: string[];
  onChange: (userIds: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
};

/** Multi-select of the firm's team members as a full-width form control. */
export const TeamMembersPicker: FC<Props> = ({
  id,
  value,
  onChange,
  disabled,
  placeholder = "Select team members",
  className
}) => {
  const { lawyerPractice } = useUserRoleAndSubscription();
  const { teamMembers, isLoading } = usePracticeTeamMembers(lawyerPractice);
  const { ensure: ensureProfiles, get: getProfile } = useUserProfiles();

  useEffect(() => {
    const ids = teamMembers.map((m) => (m?.uid ?? "").trim()).filter(Boolean);
    if (ids.length) ensureProfiles(ids);
  }, [ensureProfiles, teamMembers]);

  const selectedLabels = useMemo(
    () =>
      teamMembers
        .filter((m) => value.includes(m.uid))
        .map((m) => lawyerLabel(m)),
    [teamMembers, value]
  );

  if (isLoading && teamMembers.length === 0) {
    return <Skeleton className="h-8 w-full rounded-md" />;
  }

  if (teamMembers.length === 0) {
    return (
      <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
        No team members found. You can still continue.
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            {value.length > 0 ? (
              <>
                <AssignedUsersAvatarStack
                  userIds={value}
                  maxVisible={4}
                  nonInteractive
                />
                <span className="truncate text-xs text-muted-foreground">
                  {selectedLabels.length > 0
                    ? selectedLabels.join(", ")
                    : `${value.length} selected`}
                </span>
              </>
            ) : (
              <>
                <UserPlus className="size-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">{placeholder}</span>
              </>
            )}
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-72"
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Team members
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-72 overflow-y-auto">
          {teamMembers.map((m) => {
            const checked = value.includes(m.uid);
            const label = lawyerLabel(m) || "Unknown";
            return (
              <DropdownMenuCheckboxItem
                key={m.uid}
                checked={checked}
                className="text-xs"
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(next) =>
                  onChange(
                    next
                      ? [...value, m.uid]
                      : value.filter((uid) => uid !== m.uid)
                  )
                }
              >
                <Avatar size="sm">
                  {getProfile(m.uid)?.avatar_data_url ? (
                    <AvatarImage
                      src={getProfile(m.uid)?.avatar_data_url ?? ""}
                      alt=""
                    />
                  ) : null}
                  <AvatarFallback className="text-xs">
                    {initialsOf(label, "CB")}
                  </AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{label}</span>
                  {m.email ? (
                    <span className="truncate text-xs text-muted-foreground">
                      {m.email}
                    </span>
                  ) : null}
                </span>
              </DropdownMenuCheckboxItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
